import { useEffect, useRef } from 'react';
import { nextAttempt, endSearch } from '../utils/searchTelemetry';

// The GDS routinely answers in 30-45s and its gateway gives up around 33s with a 504, so a search
// that is merely slow surfaces as a failure. Repeating gives it another chance.
export const SEARCH_RETRY_MS = 27000;

// Two, not one: measured against the deployed API, the gateway 504s two attempts in a row and
// answers the third — 384 kB after 1.5 minutes. A single repeat left that search failing and the
// guest re-running it by hand.
export const SEARCH_RETRY_ATTEMPTS = 2;

export default function useSearchRetry(
  retryMs = SEARCH_RETRY_MS,
  attempts = SEARCH_RETRY_ATTEMPTS,
) {
  // Refs, not state: scheduling and cancelling must not re-render, and the unmount cleanup has to
  // see the current handle rather than one captured on an earlier render.
  const timer = useRef(null);
  const remaining = useRef(0);

  const cancel = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    remaining.current = 0;
    endSearch();
  };

  // Scheduling cancels whatever was pending, so a new search never leaves the previous timers armed
  // — one series per search rather than a queue of them.
  const schedule = (run) => {
    cancel();
    remaining.current = attempts;
    const arm = () => {
      timer.current = setTimeout(() => {
        timer.current = null;
        remaining.current -= 1;
        // Armed before running, so cancelling from inside run() (an answer arriving) still stops
        // the series rather than leaving the next one queued behind it.
        if (remaining.current > 0) arm();
        nextAttempt();
        run();
      }, retryMs);
    };
    if (attempts > 0) arm();
  };

  // Leaving the page cancels the series; otherwise it fires into a component that is gone.
  useEffect(() => cancel, []);

  return { schedule, cancel };
}
